import React, { useEffect, useState, useMemo } from "react"
import {
  Row,
  Table,
  Typography,
  Image,
  message,
  Tag,
  Empty, // For better empty state
} from "antd"
import {
  PrinterOutlined,
  EyeOutlined, // For image preview
  FileImageOutlined, // Placeholder for technical page image
} from "@ant-design/icons"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../firebase/config" // Adjust path as needed
import PageHeader from "../../components/Elements/PageHeader" // Adjust path as needed

const { Text } = Typography

// Define colors for machine tags for consistency
const MACHINE_COLORS = {
  Viva: "blue",
  Arsoma: "green",
  Default: "default", // Fallback color
}

export default function WinderWaitingList() {
  const [allWaitingListData, setAllWaitingListData] = useState([]) // Store all fetched data
  const [filteredInfo, setFilteredInfo] = useState({}) // Store current active filters
  const [waitingListLoading, setWaitingListLoading] = useState(false)

  useEffect(() => {
    setWaitingListLoading(true)
    const ordersRef = collection(db, "orders")

    const q = query(ordersRef, where("orderStatus", "in", [3, 4]))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const results = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setAllWaitingListData(results)
        setWaitingListLoading(false)
      },
      (error) => {
        console.error("Error fetching winder waiting list: ", error)
        message.error("Failed to load winder waiting list.")
        setWaitingListLoading(false)
      }
    )
    return () => unsubscribe()
  }, [])

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters)
  }

  // Apply client-side filtering based on filteredInfo
  // This is useful if you have multiple columns with client-side filters
  const filteredDataSource = useMemo(() => {
    // If no filters are active, return all data
    if (
      Object.keys(filteredInfo).length === 0 ||
      !Object.values(filteredInfo).some((val) => val && val.length > 0)
    ) {
      return allWaitingListData
    }
    return allWaitingListData.filter((item) => {
      // Machine filter logic (example, adapt if more filters are added)
      if (filteredInfo.machine && filteredInfo.machine.length > 0) {
        if (!filteredInfo.machine.includes(item.selectedProduct?.machine)) {
          return false
        }
      }
      // Add more filter checks here if needed for other columns
      return true
    })
  }, [allWaitingListData, filteredInfo])

  const columns = [
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      key: "machine", // Added key for React
      align: "center",
      filters: [
        // Static filters for Viva and Arsoma
        { text: "Viva", value: "Viva" },
        { text: "Arsoma", value: "Arsoma" },
      ],
      filteredValue: filteredInfo.machine || null,
      onFilter: (value, record) => record.selectedProduct?.machine === value,
      render: (machineName) => {
        if (!machineName) return <Tag color={MACHINE_COLORS.Default}>N/A</Tag>
        const color = MACHINE_COLORS[machineName] || MACHINE_COLORS.Default
        return <Tag color={color}>{machineName}</Tag>
      },
      sorter: (a, b) =>
        (a.selectedProduct?.machine || "").localeCompare(b.selectedProduct?.machine || ""),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      key: "productName", // Added key
      align: "left", // Product names can be longer
      render: (text) => <Text>{text || "N/A"}</Text>,
      sorter: (a, b) =>
        (a.selectedProduct?.product?.productName || "").localeCompare(
          b.selectedProduct?.product?.productName || ""
        ),
    },
    {
      title: "Req. Amount",
      dataIndex: "labelCount",
      key: "labelCount", // Added key
      align: "center",
      render: (text) => <Text>{text ? Number(text).toLocaleString() : "N/A"}</Text>,
      sorter: (a, b) => (a.labelCount || 0) - (b.labelCount || 0),
    },
    {
      title: "Order Nr.",
      dataIndex: "fullOrderNumber",
      key: "fullOrderNumber",
      align: "center",
      render: (text, record) => <Text strong>{text || record.orderNumber || "N/A"}</Text>,
      sorter: (a, b) => (a.fullOrderNumber || "").localeCompare(b.fullOrderNumber || ""),
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      key: "technicalPage", // Added key
      align: "center",
      width: 100,
      render: (value) =>
        value ? (
          <Image
            src={value}
            width={40} // Slightly larger for better visibility
            height={40}
            preview={{ mask: <EyeOutlined style={{ fontSize: 16 }} /> }}
            placeholder={<FileImageOutlined style={{ fontSize: 24, color: "#bfbfbf" }} />}
            alt="Tech Page"
            style={{ objectFit: "contain", borderRadius: "4px", border: "1px solid #f0f0f0" }}
          />
        ) : (
          <FileImageOutlined style={{ fontSize: 24, color: "#bfbfbf" }} />
        ),
    },
    {
      title: "Order Date",
      dataIndex: "created",
      key: "orderDate", // Added key
      align: "center",
      render: (value) =>
        value?.seconds ? new Date(value.seconds * 1000).toLocaleDateString() : "N/A",
      sorter: (a, b) => (a.created?.seconds || 0) - (b.created?.seconds || 0),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <PageHeader icon={<PrinterOutlined />} text="Winder Waiting List" />

      <Row justify="end" style={{ marginBottom: 16 }}></Row>
      <Table
        dataSource={filteredDataSource} // Use the client-side filtered data
        loading={waitingListLoading}
        rowKey={(record) => record.id}
        columns={columns}
        pagination={false}
        bordered
        size="small"
        scroll={{ x: "max-content" }}
        locale={{
          emptyText: <Empty description="No items currently in the winder waiting list." />,
        }}
        onChange={handleTableChange} // Handles filters and sorters
      />
    </div>
  )
}
